import { showUserAlert, userErrorMessage } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { v4 as uuidv4 } from "uuid";
import { searchAvailableBusesFromData } from "../services/busAvailability";
import { getKakaoRefundPolicy } from "../utils/kakaoRefundPolicy";

const KAKAO_SALES_SEEN_AT_KEY = "kakaoSalesSeenAt";
const GROUP_ROW_HUES = [42, 210, 145, 328, 258, 184, 24, 286, 112, 198, 352, 74];

function normalizeCarNumber(value = "") {
  return String(value || "").replace(/\s+/g, "").trim();
}

function shortCarNumber(value = "") {
  const digits = normalizeCarNumber(value).replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : normalizeCarNumber(value);
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function formatTime(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

function isMyCompanyRequest(request, company, carKeys) {
  if (request.selected_car_company && request.selected_car_company === company) return true;

  const selectedKeys = [
    request.selected_car_number,
    request.selected_full_car_number,
  ].map(shortCarNumber).filter(Boolean);

  return selectedKeys.some((key) => carKeys.has(key));
}

function getCompanyLabel(companyValue, companyOptions = []) {
  const option = companyOptions.find((item) => item.company === companyValue);
  return option?.companyName || companyValue || "";
}

function buildCompanyOptions(cars = [], requests = []) {
  const map = new Map();

  cars.forEach((car) => {
    if (!car.company) return;
    if (!map.has(car.company)) {
      map.set(car.company, {
        company: car.company,
        companyName: car.company_name || car.company,
      });
    } else if (car.company_name && map.get(car.company).companyName === car.company) {
      map.set(car.company, { company: car.company, companyName: car.company_name });
    }
  });

  requests.forEach((request) => {
    const companyValue = request.selected_car_company;
    if (companyValue && !map.has(companyValue)) {
      map.set(companyValue, { company: companyValue, companyName: companyValue });
    }
  });

  return Array.from(map.values()).sort((a, b) => a.companyName.localeCompare(b.companyName, "ko"));
}

function getStatusLabel(status) {
  const labels = {
    requested: "요청",
    accepted: "접수됨",
    cancelled: "취소됨",
    canceled: "취소됨",
    vendor_cancelled: "업체 취소",
    customer_cancelled: "고객 취소",
    no_available: "가능 차량 없음",
    processing: "처리중",
    completed: "예약 완료",
  };
  return labels[status] || status || "요청";
}

function getTodayDateString() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function isFutureServiceDate(value) {
  const serviceDate = formatDate(value);
  return !!serviceDate && serviceDate > getTodayDateString();
}

function isAcceptedStatus(status) {
  return status === "accepted";
}

function isCancelledStatus(status) {
  return status === "cancelled" || status === "canceled" || status === "vendor_cancelled" || status === "customer_cancelled" || status === "no_available";
}

function isCompletedStatus(status) {
  return status === "completed";
}

function isCustomerCancelledStatus(status) {
  return status === "customer_cancelled";
}

async function updateBookingRequestStatus(id, status, extra = {}) {
  const { data, error } = await supabase.rpc("update_bus_booking_request_status", {
    p_id: id,
    p_status: status,
    p_customer_cancelled_by: extra.customerCancelledBy || null,
    p_customer_cancel_reason: extra.customerCancelReason || null,
    p_refund_rate: extra.refundRate ?? null,
    p_refund_policy_text: extra.refundPolicyText || null,
  });

  if (error) throw error;
  if (!data) throw new Error("상태가 변경되지 않았습니다. DB 권한 설정을 확인해 주세요.");
  return data;
}

function getSelectedCarValue(row) {
  return row.selected_car_number || shortCarNumber(row.selected_full_car_number);
}

function getCarOptionValue(car) {
  return normalizeCarNumber(car?.fullCarNumber || car?.carNumber);
}

function getRowCarSelectValue(row, options = []) {
  const fullCarNumber = normalizeCarNumber(row?.selected_full_car_number);
  if (fullCarNumber) return fullCarNumber;

  const carNumber = normalizeCarNumber(row?.selected_car_number);
  if (!carNumber) return "";

  const matchingOption = options.find((car) => {
    return normalizeCarNumber(car.carNumber) === carNumber || shortCarNumber(car.fullCarNumber) === carNumber;
  });

  return matchingOption ? getCarOptionValue(matchingOption) : carNumber;
}

function getSelectedCarOptionFromRow(row) {
  const carNumber = getSelectedCarValue(row);
  const fullCarNumber = normalizeCarNumber(row?.selected_full_car_number);
  if (!carNumber && !fullCarNumber) return null;

  return {
    company: row.selected_car_company,
    carNumber: carNumber || shortCarNumber(fullCarNumber),
    fullCarNumber,
    carName: row.selected_car_name,
    seatType: row.selected_seat_type,
    seatCount: row.selected_seat_count,
    year: row.selected_car_year,
    driver: row.selected_driver,
    phone: row.selected_driver_phone,
    isCurrentSelection: true,
  };
}

function getDisplayCarOptions(row, options = []) {
  const current = getSelectedCarOptionFromRow(row);
  if (!current) return options;

  const currentValue = getCarOptionValue(current);
  const exists = options.some((car) => getCarOptionValue(car) === currentValue);
  return exists ? options : [current, ...options];
}

function applyCarToRequestRow(row, car) {
  return {
    ...row,
    selected_car_company: car.company,
    selected_car_number: car.carNumber,
    selected_full_car_number: car.fullCarNumber,
    selected_car_name: car.carName,
    selected_seat_type: car.seatType,
    selected_seat_count: car.seatCount,
    selected_car_year: car.year,
    selected_driver: car.driver,
    selected_driver_phone: car.phone,
  };
}

function hasSelectedBusGroup(row) {
  const buses = row?.request_state?.selectedBuses;
  return Array.isArray(buses) && buses.length > 1;
}

function getRowSelectedBus(row) {
  return {
    company: row.selected_car_company,
    carNumber: row.selected_car_number || shortCarNumber(row.selected_full_car_number),
    fullCarNumber: row.selected_full_car_number,
    carName: row.selected_car_name,
    seatType: row.selected_seat_type,
    seatCount: row.selected_seat_count,
    year: row.selected_car_year,
    driver: row.selected_driver,
    phone: row.selected_driver_phone,
  };
}

function getGroupRowColor(groupId) {
  const text = String(groupId || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  }

  const hue = GROUP_ROW_HUES[hash % GROUP_ROW_HUES.length];
  const lightness = 88 + (Math.floor(hash / GROUP_ROW_HUES.length) % 5);
  return `hsl(${hue} 82% ${lightness}%)`;
}

function toTimeInputValue(value) {
  return value ? String(value).slice(0, 5) : null;
}

function getTableHeaderClass(label) {
  const widths = {
    접수일: "min-w-36",
    배정회사: "min-w-36",
    고객명: "min-w-28",
    운행일: "w-28 min-w-28",
    출발지: "min-w-36",
    경유지: "w-24 min-w-24",
    목적지: "min-w-36",
    인원: "w-16 min-w-16",
    차량번호: "w-24 min-w-24",
    차량명: "min-w-32",
    기사명: "min-w-28",
    처리: "min-w-64",
  };
  return widths[label] || "";
}

export default function KakaoSales() {
  const [rows, setRows] = useState([]);
  const [companyCars, setCompanyCars] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [carOptionsById, setCarOptionsById] = useState({});
  const [isOperator, setIsOperator] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState("");

  const company = localStorage.getItem("email") || "";

  const groupedCounts = useMemo(() => {
    return rows.reduce((acc, row) => {
      if (row.booking_group_id) acc[row.booking_group_id] = (acc[row.booking_group_id] || 0) + 1;
      return acc;
    }, {});
  }, [rows]);

  const groupColors = useMemo(() => {
    const next = {};
    Object.keys(groupedCounts).forEach((groupId) => {
      if (groupedCounts[groupId] > 1) {
        next[groupId] = getGroupRowColor(groupId);
      }
    });
    return next;
  }, [groupedCounts]);

  const getRelatedRows = (row) => {
    if (!row?.booking_group_id) return [row];
    const relatedRows = rows.filter((item) => item.booking_group_id === row.booking_group_id);
    return relatedRows.length > 0 ? relatedRows : [row];
  };

  const getGroupLabel = (targetRows) => {
    return targetRows.length > 1 ? `같은 예약 묶음 ${targetRows.length}건` : "선택한 카카오톡 상담 요청";
  };

  const getRowStyle = (row) => {
    const backgroundColor = row.booking_group_id ? groupColors[row.booking_group_id] : "";
    return backgroundColor ? { backgroundColor } : undefined;
  };

  const ensureGroupStatus = (targetRows, predicate, message) => {
    if (targetRows.every((targetRow) => predicate(targetRow.status))) return true;
    showUserAlert(message);
    return false;
  };

  const buildAvailableCarsByRequest = (requests, cars, reservations, defaultCompany = company) => {
    const entries = requests.map((request) => {
      if (!request.date || !request.start_time || !request.passengers) {
        return [request.id, []];
      }

      const assignedCompany = request.selected_car_company || defaultCompany;
      const requestCars = assignedCompany
        ? cars.filter((car) => car.company === assignedCompany)
        : cars;
      const requestReservations = assignedCompany
        ? reservations.filter((reservation) => reservation.company === assignedCompany)
        : reservations;

      try {
        const result = searchAvailableBusesFromData({
          cars: requestCars,
          reservations: requestReservations,
          date: formatDate(request.date),
          startTime: toTimeInputValue(request.start_time),
          endTime: toTimeInputValue(request.end_time),
          passengers: request.passengers,
        });

        return [request.id, result.available || []];
      } catch (e) {
        console.warn("카톡 영업 차량 후보 조회 실패:", request.id, e);
        return [request.id, []];
      }
    });

    return Object.fromEntries(entries);
  };

  const fetchRows = async () => {
    if (!company) {
      setRows([]);
      setError("회사 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const loginEmail = localStorage.getItem("userEmail") || "";
      let operatorMode = localStorage.getItem("role") === "operator";
      if (loginEmail) {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("role")
          .eq("email", loginEmail)
          .maybeSingle();

        if (profileError) throw profileError;
        operatorMode = profile?.role === "operator" || operatorMode;
      }
      setIsOperator(operatorMode);

      let carsQuery = supabase
        .from("cars")
        .select("car_number, car_number_only, car_name, driver, phone, year, seat, seat_detail, direct, company, company_name");
      if (!operatorMode) {
        carsQuery = carsQuery.eq("company", company);
      }

      const [{ data: cars, error: carsError }, { data: requests, error: requestsError }] =
        await Promise.all([
          carsQuery,
          supabase
            .from("bus_booking_requests")
            .select("*")
            .order("created_at", { ascending: false }),
        ]);

      if (carsError) throw carsError;
      if (requestsError) throw requestsError;
      setCompanyCars(cars || []);

      const carKeys = new Set();
      (cars || []).forEach((car) => {
        [car.car_number, car.car_number_only].forEach((value) => {
          const key = shortCarNumber(value);
          if (key) carKeys.add(key);
        });
      });

      const filteredRows = operatorMode
        ? (requests || [])
        : (requests || []).filter((request) => isMyCompanyRequest(request, company, carKeys));
      const dates = [...new Set(filteredRows.map((row) => formatDate(row.date)).filter(Boolean))];
      let reservations = [];
      if (dates.length > 0) {
        let reservationQuery = supabase
          .from("reservations")
          .select("date, time, arrival_time, car_number, human_name, phone, seat, seat_detail, company")
          .in("date", dates);
        if (!operatorMode) {
          reservationQuery = reservationQuery.eq("company", company);
        }
        const { data: reservationRows, error: reservationsError } = await reservationQuery;

        if (reservationsError) throw reservationsError;
        reservations = reservationRows || [];
      }

      setCompanyOptions(buildCompanyOptions(cars || [], requests || []));
      const nextOptions = buildAvailableCarsByRequest(filteredRows, cars || [], reservations, operatorMode ? "" : company);

      const nextRows = filteredRows.map((row) => {
        if (getSelectedCarValue(row) || hasSelectedBusGroup(row)) return row;
        const firstOption = nextOptions[row.id]?.[0];
        return firstOption ? applyCarToRequestRow(row, firstOption) : row;
      });

      setRows(nextRows);
      setCarOptionsById(nextOptions);
      localStorage.setItem(KAKAO_SALES_SEEN_AT_KEY, new Date().toISOString());
      window.dispatchEvent(new Event("kakao-sales-seen-at-change"));
    } catch (e) {
      console.error("카톡 영업 조회 실패:", e);
      setError(userErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCarChange = (rowId, value) => {
    const selectedOption = (carOptionsById[rowId] || []).find((car) => getCarOptionValue(car) === value);
    if (!selectedOption) return;

    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? applyCarToRequestRow(row, selectedOption)
          : row
      )
    );
  };

  const refreshCarOptionsForRow = async (targetRow) => {
    if (!targetRow?.id || !targetRow.date || !targetRow.start_time || !targetRow.passengers) {
      setCarOptionsById((prev) => ({ ...prev, [targetRow?.id]: [] }));
      return;
    }

    try {
      const serviceDate = formatDate(targetRow.date);
      const assignedCompany = targetRow.selected_car_company || (isOperator ? "" : company);
      let reservationQuery = supabase
        .from("reservations")
        .select("date, time, arrival_time, car_number, human_name, phone, seat, seat_detail, company")
        .eq("date", serviceDate);
      if (assignedCompany) {
        reservationQuery = reservationQuery.eq("company", assignedCompany);
      }
      const { data: reservationRows, error: reservationsError } = await reservationQuery;

      if (reservationsError) throw reservationsError;

      const nextOptions = buildAvailableCarsByRequest([targetRow], companyCars, reservationRows || [], isOperator ? "" : company);
      setCarOptionsById((prev) => ({ ...prev, [targetRow.id]: nextOptions[targetRow.id] || [] }));
    } catch (e) {
      console.warn("카톡 영업 차량 후보 재조회 실패:", targetRow.id, e);
    }
  };

  const handleAssignedCompanyChange = async (rowId, value) => {
    const targetRow = rows.find((row) => row.id === rowId);
    if (!targetRow) return;

    const nextRow = {
      ...targetRow,
      selected_car_company: value || null,
      selected_car_number: null,
      selected_full_car_number: null,
      selected_car_name: null,
      selected_seat_type: null,
      selected_seat_count: null,
      selected_car_year: null,
      selected_driver: null,
      selected_driver_phone: null,
    };

    setRows((prev) => prev.map((row) => (row.id === rowId ? nextRow : row)));

    try {
      const { data, error } = await supabase
        .from("bus_booking_requests")
        .update({
          selected_car_company: value || null,
          selected_car_number: null,
          selected_full_car_number: null,
          selected_car_name: null,
          selected_seat_type: null,
          selected_seat_count: null,
          selected_car_year: null,
          selected_driver: null,
          selected_driver_phone: null,
        })
        .eq("id", rowId)
        .select("*")
        .single();

      if (error) throw error;
      await refreshCarOptionsForRow(data || nextRow);
    } catch (e) {
      console.error("카톡 영업 배정회사 변경 실패:", e);
      showUserAlert("배정회사 변경 실패: " + (e.message || String(e)));
      await fetchRows();
    }
  };

  const handleEditableChange = (rowId, field, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const handleEditableBlur = async (rowId, field, value) => {
    try {
      const payload = {
        [field]: field === "passengers"
          ? (value ? Number(value) : null)
          : value || null,
      };
      const { data, error } = await supabase
        .from("bus_booking_requests")
        .update(payload)
        .eq("id", rowId)
        .select("*")
        .single();

      if (error) throw error;
      if (data) {
        setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...data } : row)));
        if (["date", "start_time", "end_time", "passengers"].includes(field)) {
          await refreshCarOptionsForRow(data);
        }
      }
    } catch (e) {
      console.error("카톡 영업 항목 수정 실패:", e);
      showUserAlert("카톡 영업 항목 수정 실패: " + (e.message || String(e)));
    }
  };

  const handleAccept = async (row) => {
    const targetRows = getRelatedRows(row);
    if (!ensureGroupStatus(
      targetRows,
      (status) => !isAcceptedStatus(status) && !isCancelledStatus(status) && !isCompletedStatus(status),
      "같은 예약 묶음 중 이미 처리된 건이 있어 일부만 접수할 수 없습니다."
    )) return;

    if (targetRows.some((targetRow) => !targetRow.date || !targetRow.start_time)) {
      showUserAlert("운행일과 출발시간이 있어야 접수할 수 있습니다.");
      return;
    }
    if (targetRows.some((targetRow) => !getSelectedCarValue(targetRow))) {
      showUserAlert("접수할 차량번호를 선택해 주세요.");
      return;
    }

    const ok = window.confirm(`${getGroupLabel(targetRows)}을 배차처리로 접수하시겠습니까?`);
    if (!ok) return;

    setActionLoadingId(row.id);
    try {
      const groupId = uuidv4();
      const companyName = localStorage.getItem("companyName") || company;
      const reservationUser =
        row.customer_name ||
        [row.start_place, row.destination].filter(Boolean).join(" → ") ||
        "카톡 예약";
      const carCountByDate = targetRows.reduce((acc, targetRow) => {
        const key = formatDate(targetRow.date);
        if (key) acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      const reservationPayloads = targetRows.map((targetRow) => {
        const bus = getRowSelectedBus(targetRow);
        const serviceDate = formatDate(targetRow.date);
        return {
          date: serviceDate,
          time: toTimeInputValue(targetRow.start_time),
          arrival_time: toTimeInputValue(targetRow.end_time),
          start: targetRow.start_place || "",
          transit: targetRow.transit || "",
          destination: targetRow.destination || "",
          user: reservationUser,
          user_phone: targetRow.customer_phone || "",
          money: 0,
          driver_money: 0,
          memo: "카카오톡 예약 상담 접수",
          one_round: "왕복",
          number_of_car: carCountByDate[serviceDate] || 1,
          car_number: bus.carNumber || shortCarNumber(bus.fullCarNumber),
          human_name: bus.driver || "",
          phone: bus.phone || "",
          reservation_type: "일반",
          seat: bus.seatType || "",
          seat_detail: bus.seatCount ? `${bus.seatCount}` : null,
          days: 0,
          company: bus.company || targetRow.selected_car_company || company,
          company_name: getCompanyLabel(bus.company || targetRow.selected_car_company || company, companyOptions) || companyName,
          reservation_group_id: groupId,
          driver_check: false,
        };
      });

      const { error: reservationError } = await supabase.from("reservations").insert(reservationPayloads);
      if (reservationError) throw reservationError;

      const updateResults = await Promise.all(targetRows.map((targetRow) => {
        const bus = getRowSelectedBus(targetRow);
        return supabase.from("bus_booking_requests").update({
          status: "accepted",
          date: formatDate(targetRow.date) || null,
          start_time: toTimeInputValue(targetRow.start_time),
          end_time: toTimeInputValue(targetRow.end_time),
          start_place: targetRow.start_place || null,
          transit: targetRow.transit || null,
          destination: targetRow.destination || null,
          passengers: targetRow.passengers ? Number(targetRow.passengers) : null,
          selected_car_company: bus.company || targetRow.selected_car_company || company,
          selected_car_number: bus.carNumber || null,
          selected_full_car_number: bus.fullCarNumber || null,
          selected_car_name: bus.carName || null,
          selected_seat_type: bus.seatType || null,
          selected_seat_count: bus.seatCount || null,
          selected_car_year: bus.year ? String(bus.year) : null,
          selected_driver: bus.driver || null,
          selected_driver_phone: bus.phone || null,
        })
        .eq("id", targetRow.id)
        .select("id, status")
        .single();
      }));
      const requestError = updateResults.find((result) => result.error)?.error;
      if (requestError) throw requestError;
      if (updateResults.some((result) => !result.data)) throw new Error("카톡 영업 요청 상태가 접수로 변경되지 않았습니다.");

      showUserAlert("카카오톡 상담 요청을 배차처리에 접수했습니다.\n고객이 접수되었음을 인지하실 수 있게 연락을 부탁드립니다.");
      await fetchRows();
    } catch (e) {
      console.error("카톡 영업 접수 실패:", e);
      showUserAlert("접수 실패: " + (e.message || String(e)));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleVendorCancel = async (row) => {
    const targetRows = getRelatedRows(row);
    if (!ensureGroupStatus(
      targetRows,
      (status) => !isAcceptedStatus(status) && !isCompletedStatus(status) && !isCustomerCancelledStatus(status),
      "같은 예약 묶음 중 이미 처리된 건이 있어 일부만 업체 취소할 수 없습니다."
    )) return;

    const ok = window.confirm(`${getGroupLabel(targetRows)}을 이 업체에서는 운행하지 못하는 건으로 업체 취소 처리하시겠습니까?`);
    if (!ok) return;

    setActionLoadingId(row.id);
    try {
      const updatedRows = [];
      for (const targetRow of targetRows) {
        const updated = await updateBookingRequestStatus(targetRow.id, "vendor_cancelled", {
          customerCancelReason: "업체 운행 불가",
        });
        updatedRows.push(updated);
      }

      if (updatedRows.some((updated) => updated?.status === "requested" && updated?.selected_car_company && updated.selected_car_company !== company)) {
        showUserAlert("업체 취소 처리했습니다.\n조건에 맞는 다른 업체 차량으로 요청을 넘겼습니다.");
      } else {
        showUserAlert("업체 취소 처리했습니다.\n조건에 맞는 다른 업체 차량 후보가 없어 업체 취소 상태로 남았습니다.");
      }
      await fetchRows();
    } catch (e) {
      console.error("카톡 영업 업체 취소 실패:", e);
      showUserAlert("업체 취소 실패: " + (e.message || String(e)));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCustomerCancel = async (row) => {
    const targetRows = getRelatedRows(row);
    const refundPolicy = getKakaoRefundPolicy(row.date);
    const ok = window.confirm(
      [
        `${getGroupLabel(targetRows)}을 고객 요청으로 예약 자체를 취소 처리하시겠습니까?`,
        "",
        refundPolicy.description,
        refundPolicy.policyText,
      ].join("\n")
    );
    if (!ok) return;

    setActionLoadingId(row.id);
    try {
      for (const targetRow of targetRows) {
        await updateBookingRequestStatus(targetRow.id, "customer_cancelled", {
          customerCancelledBy: "company_staff",
          customerCancelReason: "업체 전화 확인 후 고객 취소",
          refundRate: refundPolicy.refundRate,
          refundPolicyText: `${refundPolicy.description} ${refundPolicy.policyText}`,
        });
      }

      showUserAlert(
        [
          "고객 취소 처리했습니다.",
          refundPolicy.description,
          "고객에게 환불 기준을 안내해 주세요.",
        ].join("\n")
      );
      await fetchRows();
    } catch (e) {
      console.error("카톡 영업 고객 취소 실패:", e);
      showUserAlert("고객 취소 실패: " + (e.message || String(e)));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleComplete = async (row) => {
    const targetRows = getRelatedRows(row);
    if (!ensureGroupStatus(
      targetRows,
      (status) => isAcceptedStatus(status),
      "같은 예약 묶음은 접수된 건 전체를 함께 예약 완료 처리해야 합니다."
    )) return;

    if (targetRows.some((targetRow) => !targetRow.end_time)) {
      showUserAlert("예약 완료 전에는 종료시간을 입력해야 합니다.");
      return;
    }

    const ok = window.confirm(`${getGroupLabel(targetRows)}의 고객 연락과 입금 확인이 완료되어 예약 완료 처리하시겠습니까?`);
    if (!ok) return;

    setActionLoadingId(row.id);
    try {
      for (const targetRow of targetRows) {
        await updateBookingRequestStatus(targetRow.id, "completed");
      }

      showUserAlert("예약 완료 처리했습니다.");
      await fetchRows();
    } catch (e) {
      console.error("카톡 영업 예약 완료 실패:", e);
      showUserAlert("예약 완료 실패: " + (e.message || String(e)));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReopen = async (row) => {
    const targetRows = getRelatedRows(row);
    const ok = window.confirm(`${getGroupLabel(targetRows)}을 다시 요청 상태로 바꾸시겠습니까?`);
    if (!ok) return;

    setActionLoadingId(row.id);
    try {
      for (const targetRow of targetRows) {
        await updateBookingRequestStatus(targetRow.id, "requested");
      }

      showUserAlert("재접수 가능한 요청 상태로 변경했습니다.");
      await fetchRows();
    } catch (e) {
      console.error("카톡 영업 재접수 실패:", e);
      showUserAlert("재접수 실패: " + (e.message || String(e)));
    } finally {
      setActionLoadingId(null);
    }
  };

  const statusCounts = useMemo(() => {
    return rows.reduce((acc, row) => {
      const status = row.status || "requested";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [rows]);

  return (
    <div>
      <Header />
      <main className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">카톡 영업</h1>
            <p className="mt-1 text-sm text-gray-500">
              {isOperator
                ? "운영사 계정으로 카카오톡 예약 상담 요청 전체를 표시합니다."
                : "카카오톡 예약 상담 요청 중 우리 회사 차량이 선택된 건만 표시합니다."}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchRows}
            disabled={loading}
            className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300"
          >
            {loading ? "조회 중..." : "새로고침"}
          </button>
        </div>

        <div className="mb-3 flex flex-wrap gap-2 text-sm">
          <span className="rounded bg-gray-100 px-2 py-1">전체 {rows.length}건</span>
          <span className="rounded bg-amber-50 px-2 py-1 text-amber-700">
            요청 {statusCounts.requested || 0}건
          </span>
          <span className="rounded bg-blue-50 px-2 py-1 text-blue-700">
            처리중 {statusCounts.processing || 0}건
          </span>
          <span className="rounded bg-green-50 px-2 py-1 text-green-700">
            접수 {statusCounts.accepted || 0}건
          </span>
          <span className="rounded bg-red-50 px-2 py-1 text-red-700">
            업체 취소 {(statusCounts.vendor_cancelled || 0) + (statusCounts.cancelled || 0) + (statusCounts.canceled || 0)}건
          </span>
          <span className="rounded bg-rose-50 px-2 py-1 text-rose-700">
            고객 취소 {statusCounts.customer_cancelled || 0}건
          </span>
          <span className="rounded bg-purple-50 px-2 py-1 text-purple-700">
            예약 완료 {statusCounts.completed || 0}건
          </span>
        </div>

        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            조회 실패: {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 text-sm">
            <thead className="bg-gray-100">
              <tr>
                {[
                  "접수일",
                  "배정회사",
                  "상태",
                  "고객명",
                  "연락처",
                  "운행일",
                  "출발시간",
                  "종료시간",
                  "출발지",
                  "경유지",
                  "목적지",
                  "인원",
                  "차량번호",
                  "차량명",
                  "기사명",
                  "처리",
                ].map((label) => (
                  <th key={label} className={`border px-2 py-2 text-center font-semibold ${getTableHeaderClass(label)}`}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={16} className="border px-3 py-8 text-center text-gray-500">
                    조회 중입니다.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={16} className="border px-3 py-8 text-center text-gray-500">
                    표시할 카카오톡 상담 요청이 없습니다.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const canEditRow = !isCancelledStatus(row.status) && !isCompletedStatus(row.status);

                  return (
                  <tr key={row.id} className="hover:bg-gray-50" style={getRowStyle(row)}>
                    <td className="border px-2 py-1 text-center">{formatDateTime(row.created_at)}</td>
                    <td className="min-w-36 border px-1 py-1 text-center">
                      {isOperator && canEditRow ? (
                        <select
                          value={row.selected_car_company || ""}
                          onChange={(e) => handleAssignedCompanyChange(row.id, e.target.value)}
                          className="w-32 rounded border px-1 py-1 text-sm"
                        >
                          <option value="">미배정</option>
                          {companyOptions.map((option) => (
                            <option key={option.company} value={option.company}>
                              {option.companyName}
                            </option>
                          ))}
                        </select>
                      ) : (
                        getCompanyLabel(row.selected_car_company, companyOptions)
                      )}
                    </td>
                    <td className="border px-2 py-1 text-center">{getStatusLabel(row.status)}</td>
                    <td className="min-w-28 border px-2 py-1 text-center">{row.customer_name || ""}</td>
                    <td className="border px-2 py-1 text-center">{row.customer_phone || ""}</td>
                    <td className="w-28 min-w-28 border px-1 py-1 text-center">
                      <input
                        type="date"
                        value={formatDate(row.date)}
                        onChange={(e) => handleEditableChange(row.id, "date", e.target.value)}
                        onBlur={(e) => handleEditableBlur(row.id, "date", e.target.value)}
                        disabled={!canEditRow}
                        className="w-[118px] rounded border px-1 py-1 text-sm disabled:bg-gray-100"
                      />
                    </td>
                    <td className="border px-2 py-1 text-center">
                      <input
                        type="time"
                        value={formatTime(row.start_time)}
                        onChange={(e) => handleEditableChange(row.id, "start_time", e.target.value)}
                        onBlur={(e) => handleEditableBlur(row.id, "start_time", e.target.value)}
                        disabled={!canEditRow}
                        className="w-24 rounded border px-1 py-1 text-sm disabled:bg-gray-100"
                      />
                    </td>
                    <td className="border px-2 py-1 text-center">
                      <input
                        type="time"
                        value={formatTime(row.end_time)}
                        onChange={(e) => handleEditableChange(row.id, "end_time", e.target.value)}
                        onBlur={(e) => handleEditableBlur(row.id, "end_time", e.target.value)}
                        disabled={!canEditRow}
                        className="w-24 rounded border px-1 py-1 text-sm disabled:bg-gray-100"
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <input
                        type="text"
                        value={row.start_place || ""}
                        onChange={(e) => handleEditableChange(row.id, "start_place", e.target.value)}
                        onBlur={(e) => handleEditableBlur(row.id, "start_place", e.target.value)}
                        disabled={!canEditRow}
                        className="w-36 rounded border px-1 py-1 text-sm disabled:bg-gray-100"
                      />
                    </td>
                    <td className="w-24 min-w-24 border px-1 py-1">
                      <input
                        type="text"
                        value={row.transit || ""}
                        onChange={(e) => handleEditableChange(row.id, "transit", e.target.value)}
                        onBlur={(e) => handleEditableBlur(row.id, "transit", e.target.value)}
                        disabled={!canEditRow}
                        className="w-20 rounded border px-1 py-1 text-sm disabled:bg-gray-100"
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <input
                        type="text"
                        value={row.destination || ""}
                        onChange={(e) => handleEditableChange(row.id, "destination", e.target.value)}
                        onBlur={(e) => handleEditableBlur(row.id, "destination", e.target.value)}
                        disabled={!canEditRow}
                        className="w-36 rounded border px-1 py-1 text-sm disabled:bg-gray-100"
                      />
                    </td>
                    <td className="w-16 min-w-16 border px-1 py-1 text-center">
                      <input
                        type="number"
                        min="1"
                        value={row.passengers || ""}
                        onChange={(e) => handleEditableChange(row.id, "passengers", e.target.value)}
                        onBlur={(e) => handleEditableBlur(row.id, "passengers", e.target.value)}
                        disabled={!canEditRow}
                        className="w-12 rounded border px-1 py-1 text-right text-sm disabled:bg-gray-100"
                      />
                    </td>
                    <td className="w-24 min-w-24 border px-1 py-1 text-center">
                      {(() => {
                        const displayOptions = getDisplayCarOptions(row, carOptionsById[row.id] || []);
                        return (
                          <select
                            value={getRowCarSelectValue(row, displayOptions)}
                            onChange={(e) => handleCarChange(row.id, e.target.value)}
                            disabled={!canEditRow}
                            className="w-20 rounded border px-1 py-1 text-sm"
                          >
                            {!getSelectedCarValue(row) && <option value="">선택</option>}
                            {displayOptions.map((car, index) => (
                              <option key={`${row.id}-${getCarOptionValue(car)}-${index}`} value={getCarOptionValue(car)}>
                                {car.carNumber}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                    </td>
                    <td className="min-w-32 border px-2 py-1 text-center">{row.selected_car_name || ""}</td>
                    <td className="min-w-28 border px-2 py-1 text-center">{row.selected_driver || ""}</td>
                    <td className="min-w-64 border px-2 py-1 text-center">
                      {isAcceptedStatus(row.status) ? (
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-semibold text-blue-700">접수됨</span>
                          <button
                            type="button"
                            onClick={() => handleComplete(row)}
                            disabled={actionLoadingId === row.id}
                            className="rounded bg-purple-600 px-2 py-1 text-xs font-semibold text-white disabled:bg-gray-300"
                          >
                            예약 완료
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCustomerCancel(row)}
                            disabled={actionLoadingId === row.id}
                            className="rounded bg-rose-600 px-2 py-1 text-xs font-semibold text-white disabled:bg-gray-300"
                          >
                            고객 취소
                          </button>
                        </div>
                      ) : isCompletedStatus(row.status) ? (
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-semibold text-purple-700">예약 완료</span>
                          <button
                            type="button"
                            onClick={() => handleCustomerCancel(row)}
                            disabled={actionLoadingId === row.id}
                            className="rounded bg-rose-600 px-2 py-1 text-xs font-semibold text-white disabled:bg-gray-300"
                          >
                            고객 취소
                          </button>
                        </div>
                      ) : isCancelledStatus(row.status) ? (
                        isCustomerCancelledStatus(row.status) ? (
                          <span className="font-semibold text-rose-700">
                            고객 취소{row.refund_rate !== null && row.refund_rate !== undefined ? `(${row.refund_rate}%)` : ""}
                          </span>
                        ) : isFutureServiceDate(row.date) ? (
                          <button
                            type="button"
                            onClick={() => handleReopen(row)}
                            disabled={actionLoadingId === row.id}
                            className="rounded bg-amber-500 px-2 py-1 text-xs font-semibold text-white disabled:bg-gray-300"
                          >
                            재접수
                          </button>
                        ) : (
                          <span className="font-semibold text-red-600">취소됨</span>
                        )
                      ) : (
                        <div className="flex justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleAccept(row)}
                            disabled={actionLoadingId === row.id}
                            className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white disabled:bg-gray-300"
                          >
                            접수
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVendorCancel(row)}
                            disabled={actionLoadingId === row.id}
                            className="rounded bg-red-500 px-2 py-1 text-xs font-semibold text-white disabled:bg-gray-300"
                          >
                            업체 취소
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCustomerCancel(row)}
                            disabled={actionLoadingId === row.id}
                            className="rounded bg-rose-600 px-2 py-1 text-xs font-semibold text-white disabled:bg-gray-300"
                          >
                            고객 취소
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
